"use client";

import React from "react";
import dayjs from "dayjs";
import { useAtomValue } from "jotai";
import { Swiper, SwiperSlide } from "swiper/react";
import { useAction } from "next-safe-action/hooks";
import { Mousewheel, Navigation } from "swiper/modules";

import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Paragraph } from "@/components/ui/typography";

import LiveClass from "@/components/card/live-class";
import SwiperNav from "@/components/swiper-nav";

import { isAuthenticatedAtom } from "@/store/auth";
import { fetchLiveClasses } from "@/fetchers/live-class";

import { cn } from "@/lib/utils";

function TodayLiveClasses({ className, ...props }) {
  const navPrevRef = React.useRef(null);
  const navNextRef = React.useRef(null);

  const [swiper, setSwiper] = React.useState(null);

  const isAuthenticated = useAtomValue(isAuthenticatedAtom);

  const {
    result,
    isExecuting,
    execute: fetchClasses,
  } = useAction(fetchLiveClasses);

  React.useEffect(() => {
    if (!isAuthenticated) return;
    const todayDate = dayjs().format("YYYY-MM-DD");

    fetchClasses({ startDate: todayDate, endDate: todayDate });
  }, [isAuthenticated, fetchClasses]);

  const liveClasses = React.useMemo(() => {
    if (!result || !result?.data) return [];

    let todayLiveClasses = Object.values(result?.data || {}).flatMap(
      (item) => item
    );

    // Sort live classes: ongoing first, then by start time
    todayLiveClasses = todayLiveClasses
      .sort((a, b) => a.startTime - b.startTime)
      .sort((liveClass) => {
        const isNotEndedClass = dayjs(liveClass.startTime).isAfter(dayjs());

        return isNotEndedClass === false || liveClass.status === 3 ? 1 : -1;
      });

    return todayLiveClasses;
  }, [result]);

  if (!liveClasses?.length) return null;

  return (
    <Card
      className={cn("container-studyiq pt-0 pb-6 gap-y-4", className)}
      {...props}
    >
      <CardHeader className="rounded-t-lg md:shadow-card">
        <CardTitle>
          <Paragraph variant="b2">Today&apos;s Live Classes</Paragraph>
        </CardTitle>
      </CardHeader>

      <CardContent className="relative px-0 md:px-3">
        {!isExecuting && liveClasses && (
          <Swiper
            modules={[Mousewheel, Navigation]}
            spaceBetween={12}
            slidesPerView={3}
            slidesOffsetBefore={10}
            slidesOffsetAfter={10}
            mousewheel={{ forceToAxis: true }}
            onSwiper={setSwiper}
            breakpoints={{
              0: {
                slidesPerView: 1,
              },
              480: {
                slidesPerView: 1.5,
              },
              600: {
                slidesPerView: 1.8,
              },
              900: {
                slidesPerView: 2.5,
              },
              1100: {
                slidesPerView: 3,
              },
            }}
          >
            {liveClasses?.map((liveClass) => (
              <SwiperSlide
                key={`live-class-${liveClass.scheduleId}`}
                className="py-3"
              >
                <LiveClass liveClass={liveClass} />
              </SwiperSlide>
            ))}
          </Swiper>
        )}

        <SwiperNav
          swiper={swiper}
          nextNavRef={navNextRef}
          prevNavRef={navPrevRef}
          className="hidden md:flex absolute top-1/2 left-0 -translate-y-1/2 w-full justify-between z-10"
          iconClassName="data-[nav=prev]:-translate-x-full data-[nav=next]:translate-x-full"
        />
      </CardContent>
    </Card>
  );
}

export default TodayLiveClasses;
